import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Plus } from "lucide-react";
import { useTran, useFormDirty } from "@/hooks";

import { AppDialog, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";
import { CompanySetupDialog, useCompanyStore } from "@/features/company";
import { useUserStore, useUserListDerived } from "../hooks";
import PermissionOverridesSection from "./PermissionOverridesSection";
import { Switch } from "@/components/ui/switch";
import { OrgTreeNodeBreadcrumb } from "@/components/common/OrgTreeNodeBreadcrumb";

const ADMIN_ROLES = ["system_admin", "business_unit"];
const COMPANY_CREATION_ROLES = ["super_admin", "business_unit", "reseller"];

const USERNAME_ALLOWED_REGEX = /^[a-z0-9]+$/;

const SYSTEM_ROLE_OPTIONS = [
  { value: "system_admin", label: "System Admin" },
  { value: "business_unit", label: "Business Unit" },
  { value: "super_admin", label: "Super Admin" },
  { value: "reseller", label: "Reseller" },
  { value: "solution_provider", label: "Solution Provider" },
  { value: "bank_admin", label: "Bank Admin" },
  { value: "sme_user", label: "SME User" },
];

const hasPermissionOverrideSelection = (permissionOverrides) => {
  if (permissionOverrides?.mode !== "override") return true;

  const allow = Array.isArray(permissionOverrides?.allow)
    ? permissionOverrides.allow
    : [];
  const deny = Array.isArray(permissionOverrides?.deny)
    ? permissionOverrides.deny
    : [];

  return allow.length > 0 || deny.length > 0;
};

const getCompanyId = (company) =>
  company?.id ?? company?._id ?? company?.companyId ?? null;

const clearSessionStorage = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.clear();
  } catch {
    // Ignore storage failures.
  }
};

const CreateUserDialog = observer(({ open, onOpenChange }) => {
  const { t } = useTran();
  const { userRole } = useAuthStore();
  const [companySetupOpen, setCompanySetupOpen] = useState(false);
  const {
    createForm,
    createLoading,
    createError,
    resetCreateForm,
    setCreateField,
    createManagedUser,
    setCreatePermissionOverrides,
    loadUsers,
  } = useUserStore();
  const {
    roleOptions,
    rolesWithPermissions,
    createPermissionModules,
    companyOptions,
    companyLoading,
    organizationTree,
    organizationLoading,
    rolePermissionsToAllow,
    buildAllowAllPermissions,
  } = useUserListDerived();
  const {
    fetchPublicCompanies,
    reset: resetCompanySetup,
    setCompany,
    upsertCompany,
  } = useCompanyStore();
  const isCompanyAdmin = ADMIN_ROLES.includes(userRole);

  const isDirty = useFormDirty(createForm, open);

  const parentCompanyId = useMemo(() => {
    const parentNode = organizationTree.find(
      (node) => node.unitLevel === "parent_company",
    );
    return parentNode?.id || parentNode?._id || parentNode?.key || null;
  }, [organizationTree]);

  const orgSelectValue = useMemo(() => {
    const ids = [...(createForm.organizationUnitId || [])];
    if (createForm.assignedToParentCompany && parentCompanyId) {
      if (!ids.includes(parentCompanyId)) ids.unshift(parentCompanyId);
    }
    return ids;
  }, [
    createForm.organizationUnitId,
    createForm.assignedToParentCompany,
    parentCompanyId,
  ]);

  const handleOrgChange = (selectedIds) => {
    const isParentSelected =
      parentCompanyId && selectedIds.includes(parentCompanyId);
    const orgUnitIds = parentCompanyId
      ? selectedIds.filter((id) => id !== parentCompanyId)
      : selectedIds;
    setCreateField("assignedToParentCompany", isParentSelected);
    setCreateField("organizationUnitId", orgUnitIds);
  };

  const resolvedRoleOptions = useMemo(() => {
    if (Array.isArray(roleOptions) && roleOptions.length > 0) {
      return roleOptions;
    }
    return SYSTEM_ROLE_OPTIONS.map((role) => ({
      ...role,
      label: t(role.label),
    }));
  }, [roleOptions, t]);

  const usernameValue = createForm.username || "";
  const usernameError =
    usernameValue.length > 0 && usernameValue.length < 3
      ? t("Username must be at least 3 characters.")
      : usernameValue && !USERNAME_ALLOWED_REGEX.test(usernameValue)
        ? t(
            "Username can only contain lowercase letters and numbers without spaces.",
          )
        : "";

  const requiresOrganization =
    !isCompanyAdmin && createForm.role !== "business_unit";
  const requiresCompany = isCompanyAdmin;
  const showCustomRoleNameField = createForm.role === "super_admin";
  const selectedRole = String(createForm.role || "")
    .trim()
    .toLowerCase();
  const canCreateNewCompany = COMPANY_CREATION_ROLES.includes(selectedRole);
  const canSave =
    createForm.displayName &&
    createForm.username &&
    !usernameError &&
    createForm.email &&
    createForm.role &&
    (!requiresOrganization ||
      createForm.organizationUnitId.length > 0 ||
      createForm.assignedToParentCompany) &&
    (!requiresCompany || createForm.companyId) &&
    createForm.permissionOverrides &&
    !createLoading;

  const handleDialogOpenChange = (nextOpen) => {
    if (!nextOpen) {
      setCompanySetupOpen(false);
      resetCreateForm();
    }
    onOpenChange(nextOpen);
  };

  const handleChange = (field) => (event) => {
    setCreateField(field, event.target.value);
  };
  // Orginal Code
  // const handleSelect = (field) => (input) => {
  //   const value = input?.target ? input.target.value : input;
  //   if (field === "companyId") {
  //     setCreateField("companyId", value);
  //     setCreateField("organizationUnitId", "");
  //     return;
  //   }
  //   setCreateField(field, value);
  // };

  const handleSelect = (field) => (input) => {
    const value = input?.target ? input.target.value : input;

    if (field === "companyId") {
      setCreateField("companyId", value);

      // clear selected organizations when company changes
      setCreateField("organizationUnitId", []);
      return;
    }

    if (field === "role") {
      setCreateField("role", value);
      if (value !== "super_admin") {
        setCreateField("customRoleName", "");
      }
      return;
    }

    setCreateField(field, value);
  };

  const emptyCompanyLabel = t("No companies available");

  const openCompanySetupDialog = () => {
    resetCompanySetup({ persist: false });
    setCompany(null);
    setCompanySetupOpen(true);
  };

  const handleCompanyCreated = async (createdCompany) => {
    const createdCompanyId = getCompanyId(createdCompany);
    if (createdCompanyId) {
      upsertCompany(createdCompany);
      setCreateField("companyId", String(createdCompanyId));
      setCreateField("organizationUnitId", []);
    }

    try {
      await fetchPublicCompanies({ force: true });
      if (createdCompanyId) {
        upsertCompany(createdCompany);
        setCreateField("companyId", String(createdCompanyId));
      }
    } catch (error) {
      if (!createdCompanyId) {
        showToast.error(error?.message || t("Unable to refresh company list."));
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (createLoading) return;
    if (!hasPermissionOverrideSelection(createForm.permissionOverrides)) {
      showToast.error(
        t("Select at least one permission when mode is Override."),
      );
      return;
    }

    try {
      const payload = {
        displayName: createForm.displayName,
        username: createForm.username,
        email: createForm.email,
        role: createForm.role,
        customRoleName: showCustomRoleNameField
          ? createForm.customRoleName
          : undefined,
        organizationUnitId: isCompanyAdmin
          ? null
          : createForm.organizationUnitId,
        assignedToParentCompany: isCompanyAdmin
          ? undefined
          : createForm.assignedToParentCompany,
        companyId: isCompanyAdmin ? createForm.companyId : undefined,
        permissionOverrides: createForm.permissionOverrides,
        email2FAEnabled: createForm.email2FAEnabled,
      };

      //console.log("Create User Info : ", toJS(payload));
      const createdUser = await createManagedUser(payload);
      loadUsers();
      clearSessionStorage();
      if (createdUser?.temporaryPassword) {
        // showToast.success(
        //   t("Temporary password: {{password}}", {
        //     password: createdUser.temporaryPassword,
        //   }),
        // ); kyn modified 2026-02-08
      }
      onOpenChange(false);
    } catch (error) {
      const message =
        error?.message || createError || t("Unable to create user.");
      showToast.error(message);
    }
  };

  useEffect(() => {
    const roleKey = createForm.role;
    const mode = createForm.permissionOverrides?.mode;

    if (!roleKey) return;
    if (mode !== "inherit") return;

    const selectedRole = rolesWithPermissions.find(
      (r) => r.name === roleKey || r._id === roleKey,
    );

    if (!selectedRole) return;
    let permissions = selectedRole.isPermitAll
      ? buildAllowAllPermissions(createPermissionModules)
      : selectedRole.permissions || [];
    const allow = rolePermissionsToAllow(permissions);
    const currentOverrides = createForm.permissionOverrides || {};
    const currentAllow = Array.isArray(currentOverrides.allow)
      ? currentOverrides.allow
      : [];
    const currentDeny = Array.isArray(currentOverrides.deny)
      ? currentOverrides.deny
      : [];
    const isSameAllow =
      currentAllow.length === allow.length &&
      currentAllow.every((item, index) => item === allow[index]);
    const isSameInheritOverrides =
      currentOverrides.mode === "inherit" &&
      isSameAllow &&
      currentDeny.length === 0;
    if (isSameInheritOverrides) {
      return;
    }

    setCreateField("permissionOverrides", {
      mode: "inherit",
      allow,
      deny: [],
    });
  }, [
    createForm.role,
    createForm.permissionOverrides,
    createForm.permissionOverrides?.mode,
    rolesWithPermissions,
    rolePermissionsToAllow,
    buildAllowAllPermissions,
    createPermissionModules,
    setCreateField,
  ]);

  useEffect(() => {
    const mode = createForm.permissionOverrides?.mode;
    if (mode === "override") {
      setCreateField("permissionOverrides", {
        mode: "override",
        allow: [],
        deny: [],
      });
    }
  }, [createForm.permissionOverrides?.mode, setCreateField]);

  return (
    <>
      <AppDialog
        open={open}
        onOpenChange={handleDialogOpenChange}
        title={t("Create user")}
        description={t("Add a new user and set their basic details.")}
        size="xl"
        isDirty={isDirty}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Main 2-Column Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 items-start">
            {/* LEFT SIDE: Identity & Organization */}
            <div className="space-y-4">
              {/* Display Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t("Display name")}{" "}
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  value={createForm.displayName}
                  onChange={handleChange("displayName")}
                  placeholder={t("Enter display name")}
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t("Email")} <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  type="email"
                  value={createForm.email}
                  onChange={handleChange("email")}
                  placeholder={t("Enter email")}
                  required
                />
              </div>

              {/* Custom Role Name (Conditional) */}
              {showCustomRoleNameField && (
                <div className="space-y-2 animate-in fade-in duration-200">
                  <label className="text-sm font-medium text-foreground">
                    {t("Custom role name")}
                  </label>
                  <Input
                    value={createForm.customRoleName}
                    onChange={handleChange("customRoleName")}
                    placeholder={t("Enter custom role name")}
                  />
                </div>
              )}

              {/* 2FA Toggle */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t("Enable Email 2FA")}
                </label>

                <div className="flex min-h-10 items-center rounded-md border px-3 py-2">
                  <Switch
                    checked={createForm.email2FAEnabled}
                    onCheckedChange={(checked) =>
                      setCreateField("email2FAEnabled", checked)
                    }
                  />
                </div>
              </div>

              {/* Company or Organization */}
              {isCompanyAdmin ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t("Company Name")}

                    <span className="text-destructive ml-1">*</span>
                  </label>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                    <SelectField
                      id="companyId"
                      className="min-w-0 flex-1"
                      value={createForm.companyId || undefined}
                      onChange={handleSelect("companyId")}
                      options={companyOptions}
                      placeholder={t("Select a company")}
                      disabled={companyLoading}
                      emptyLabel={emptyCompanyLabel}
                    />

                    {canCreateNewCompany ? (
                      <Button
                        type="button"
                        variant="default"
                        onClick={openCompanySetupDialog}
                        disabled={createLoading}
                        className="w-full gap-2 sm:w-auto"
                      >
                        <Plus className="h-4 w-4" />

                        {t("Create New Company")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t("Organizations")}

                    <span className="text-destructive ml-1">*</span>
                  </label>
                  <OrgTreeNodeBreadcrumb
                    tree={organizationTree}
                    value={orgSelectValue}
                    onChange={handleOrgChange}
                    placeholder={t("Select organizations")}
                    disabled={organizationLoading}
                    includeParentCompany={true}
                  />
                </div>
              )}
            </div>

            {/* RIGHT SIDE: Access & System */}
            <div className="space-y-4">
              {/* Username */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t("Username")}{" "}
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  value={createForm.username}
                  onChange={handleChange("username")}
                  placeholder={t("Enter username")}
                  required
                />
                {usernameError && (
                  <p className="text-xs text-destructive">{usernameError}</p>
                )}
              </div>

              {/* Role */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t("Role")} <span className="text-destructive ml-1">*</span>
                </label>
                <SelectField
                  id="role"
                  value={createForm.role || undefined}
                  onChange={handleSelect("role")}
                  options={resolvedRoleOptions}
                  placeholder={t("Select a role")}
                />
              </div>

              {/* Permissions Section */}
              {createForm.role && (
                <div className="space-y-2 animate-in slide-in-from-top-1 duration-300">
                  <label className="text-sm font-medium text-foreground">
                    {t("Permissions")}{" "}
                    <span className="text-destructive ml-1">*</span>
                  </label>
                  <div>
                    <PermissionOverridesSection
                      isUserCreatedRole={true}
                      value={createForm.permissionOverrides}
                      modules={createPermissionModules}
                      onChange={setCreatePermissionOverrides}
                      checkBoxDisable={
                        createForm.permissionOverrides?.mode === "inherit"
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={createLoading}
              className="w-full sm:w-auto"
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={!canSave}
              className="w-full sm:w-auto"
            >
              {createLoading ? t("Saving...") : t("Save")}
            </Button>
          </div>
        </form>
      </AppDialog>
      <CompanySetupDialog
        open={open && companySetupOpen}
        user={null}
        onClose={() => setCompanySetupOpen(false)}
        onCompleted={handleCompanyCreated}
      />
    </>
  );
});

export default CreateUserDialog;
